<template>
  <div>
    <div
      v-if="showFilters"
      class="filters__options"
    >
      <template v-for="(filter, index) in filters">
        <div
          v-if="filter.options || filter.switch || filter.threeWaySwitch"
          :key="filter.name"
          class="input--select px-3 pt-4"
          :class="{'input--multiselect':filter.multiple, 'input--switch':filter.switch }"
        >
          <template v-if="filter.switch">
            <div class="input-label">
              {{ filter.text }}
            </div>
            <v-switch
              v-model="filter.filterBy"
              :false-value="filter.falseValue !== undefined ? filter.falseValue : false"
              :true-value="{value: filter.trueValue || true, name: filter.text}"
              hide-details
              @change="changeFilters()"
            />
          </template>
          <v-autocomplete
            v-else-if="filter.threeWaySwitch"
            v-model="filter.filterBy"
            :items="[
              {value: true, name: filter.trueLabel || 'Tak'},
              {value: false, name: filter.falseLabel || 'Nie'}
            ]"
            v-bind="autocompleteProps(filter)"
            @change="changeFilters(index)"
          />
          <v-autocomplete
            v-else
            v-model="filter.filterBy"
            :items="filter.options"
            :search-input.sync="searchQuery[index]"
            v-bind="autocompleteProps(filter)"
            @change="debouncedChangeFilters(index)"
          >
            <template #selection="{ index: idx, item }">
              <span
                v-if="filter.filterBy.length < 2 || !filter.multiple"
                class="input--long mr-1 ml-1"
              >
                {{ item.name }}
              </span>
              <span
                v-else-if="idx === 0"
                class="mr-1 ml-1"
              >
                {{ filtersDeclension(filter.filterBy.length) }}
              </span>
            </template>
          </v-autocomplete>
        </div>
      </template>
    </div>
    <div
      v-if="filters.length"
      class="filters__selected-row"
    >
      <div class="filters__selected">
        <template v-for="filter in filters">
          <template v-if="Array.isArray(filter.filterBy)">
            <v-chip
              v-for="singleFilter in filter.filterBy"
              :key="singleFilter.name"
              :value="singleFilter.value || singleFilter"
              v-bind="chipProps(filter)"
              @click:close="unpinMultipleFilter(filter.name, singleFilter)"
            >
              {{ chipLabel(singleFilter) }}
            </v-chip>
          </template>
          <v-chip
            :key="filter.name"
            v-else-if="filter.filterBy"
            :value="filter.filterBy"
            v-bind="chipProps(filter)"
            @click:close="filter.filterBy = ''"
            @update:active="changeFilters()"
          >
            {{ chipLabel(filter.filterBy) }}
          </v-chip>
        </template>
      </div>
      <div v-if="isAnyFilterSelected(tableName)">
        <v-btn
          :outlined="filtersEnabled"
          color="primary"
          dark
          hide-details
          small
          class="mr-2 my-1"
          @click="filtersEnabled = !filtersEnabled"
        >
          {{ filtersEnabled ? 'Dezaktywuj' : 'Aktywuj' }}
        </v-btn>
        <v-btn
          v-if="isAnyFilterSelected(tableName)"
          color="primary"
          dark
          hide-details
          small
          class="mr-4 my-1"
          @click="clearTableFilters({ tableName })"
        >
          Wyczyść
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import filtersMixin from '../../mixins/filtersMixin'
import { mapState, mapActions, mapGetters } from 'vuex'
import { declensionName } from '../../utils'
import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'

export default {
  mixins: [filtersMixin], // filters
  data: () => ({
    searchQuery: [],
    initialFiltersState: [],
    changeFiltersDebounce: null,
  }),
  props: {
    tableName: {
      type: String,
      required: true,
    },
    disableFetch: {
      type: Boolean,
      deafult: false,
    },
    currentTab: {
      type: String,
      default: '',
    },
  },
  mounted () {
    this.initialFiltersState = cloneDeep(this.filters)
    // we are caching initial state of filters to compare it with current state
    // and decide if we should send request to API on blur event
  },
  computed: {
    ...mapState({
      showFilters (state) {
        return state[this.tableName].showFilters
      },
    }),
    ...mapGetters({
      isAnyFilterSelected: 'tables/isAnyFilterSelected',
    }),
    config () {
      return this.$store.getters['tables/getTableConfig'](this.tableName)
    },
    allFilters () {
      return this.config.filters.filter((filter) => filter?.tabs?.includes(this.currentTab) || !filter.tabs)
    },
    areFiltersChanged () {
      return JSON.stringify(this.filters) !== JSON.stringify(this.initialFiltersState)
    },
    filtersEnabled: {
      get () {
        return this.config.filtersEnabled
      },
      set (status) {
        this.setTableFiltersStatus({ status, tableName: this.tableName, noApi: this.disableFetch })
        if (this.disableFetch) this.$emit('fetch')
      },
    },
  },
  methods: {
    ...mapActions({
      setTableFilters: 'tables/setTableFilters',
      clearTableFilters: 'tables/clearTableFilters',
      setTableFiltersStatus: 'tables/setTableFiltersStatus',
    }),
    changeFilters (index) {
      if (!this.areFiltersChanged) return
      if (index || index === 0) this.searchQuery[index] = ''
      const { filters, tableName, disableFetch } = this
      this.setTableFilters({ filters, tableName, disableFetch })
      this.initialFiltersState = cloneDeep(filters)
      if (!this.isAnyFilterSelected(this.tableName)) this.setTableFiltersStatus({ status: true, tableName: this.tableName, noApi: true })
      if (this.disableFetch) this.$emit('fetch')
    },
    unpinMultipleFilter (filterName, filterBy) {
      const filterIndex = this.filters.findIndex((filter) => filter.name === filterName)
      const getFilterName = filter => filter.name || filter
      this.filters[filterIndex].filterBy = this.filters[filterIndex].filterBy.filter(
        (filter) => getFilterName(filter) !== getFilterName(filterBy)
      )
      this.changeFilters()
    },
    filtersDeclension (counter) {
      return declensionName(counter, 'element', 'elementy', 'elementów')
    },
    chipLabel (filterBy, maxLength = 50) {
      const label = filterBy.name || filterBy
      if (label?.length > maxLength) return label.substring(0, maxLength).trim() + '...'
      return label
    },
    autocompleteProps (filter) {
      return {
        outlined: true,
        hideDetails: true,
        itemText: 'name',
        placeholder: 'Wybierz',
        appendIcon: 'mdi-chevron-down',
        returnObject: true,
        menuProps: { maxWidth: 'unset' },
        dense: true,
        clearable: !filter.notClearable || false,
        disabled: filter.disabled,
        filter: filter.specialFilter,
        label: filter.text,
        multiple: filter.multiple
      }
    },
    debouncedChangeFilters(index) {
      if (this.changeFiltersDebounce && this.changeFiltersDebounce?.cancel) {
        this.changeFiltersDebounce.cancel()
      }
      this.changeFiltersDebounce = debounce(() => {
        this.changeFilters(index)
      }, 400)

      this.changeFiltersDebounce()
    },
    chipProps (filter) {
      return {
        class: ['ma-2', { disabled: !this.filtersEnabled }],
        closeIcon: filter?.notClearable ? '' : 'mdi-close',
        color: 'primary',
        close: true,
        outlined: true,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.filters__options::v-deep .v-list-item__title {
  max-width: 450px;
}
::v-deep .v-list-item__content > .v-list-item__title {
  white-space: wrap !important;
  text-overflow: clip !important;
  white-space: break-spaces !important;
}
</style>
